import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { upsertLeadOrContactAndCheckDOI } from '@adornis/digitale-helden-shared/api/Lead/upsertLeadOrContactAndCheckDOI.js';
import '@adornis/digitale-helden-shared/client/theme/d-button.js';
import '@adornis/digitale-helden-shared/client/theme/d-checkbox.js';
import { DubniumDialog } from '@adornis/digitale-helden-shared/client/theme/d-dialog.js';
import '@adornis/digitale-helden-shared/client/theme/d-dropdown-selection.js';
import '@adornis/digitale-helden-shared/client/theme/d-icon.js';
import '@adornis/digitale-helden-shared/client/theme/d-link.js';
import '@adornis/digitale-helden-shared/client/theme/d-textarea.js';
import '@adornis/digitale-helden-shared/client/theme/d-tooltip.js';
import { Lead } from '@adornis/digitale-helden-shared/db/Lead.js';
import type { LASTranslationDictionary } from '@adornis/digitale-helden-shared/db/translation.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { triggerSuccessIcon } from '../../../client/theme/animations/success-animation.js';
import { ContactFormData } from '../../db/ContactFormData.js';

@customElement('las-form-contact')
export class LASFormContact extends ChemistryLitElement {
  @state() private readonly _translation = new TranslationController<LASTranslationDictionary>(this);
  @state() private _acceptedPrivacy = false;
  @state() private readonly _formController = this._initFormController();
  @state() private repeatEmail: string | undefined = undefined;

  @state() private _isFinished = false;

  override render() {
    if (this._isFinished)
      return html`
        <las-success
          ?show-email-note=${this._formController.document?.isNewsletter || this._formController.document?.isWebinar}
        ></las-success>
      `;

    return html`
      <d-flex space="md" ${css({ position: 'relative' })}>
        <!-- Vorname -->
        <d-input
          placeholder=${this._translation.translate('first_name')}
          placeholder-mode="static-floating"
          ${this._formController.field('firstName')}
        ></d-input>
        <!-- Nachname -->
        <d-input
          placeholder=${this._translation.translate('last_name')}
          ${this._formController.field('lastName')}
        ></d-input>
        <!-- Email -->
        <d-input
          placeholder=${this._translation.translate('e_mail')}
          ${this._formController.field('email')}
          @value-picked=${() => this.requestUpdate()}
        ></d-input>
        <!-- Email Wiederholung -->
        <d-input
          placeholder=${this._translation.translate('e_mail_repeat')}
          type="email"
          onpaste="return false"
          ondrop="return false"
          .value=${this.repeatEmail}
          @value-picked=${(e: ValueEvent<string>) => {
            this.repeatEmail = e.detail.value;
          }}
          error=${ifDefined(
            this._formController.document?.email === this.repeatEmail || this.repeatEmail === undefined
              ? undefined
              : this._translation.translate('email_repeat_error'),
          )}
        ></d-input>
        <!-- Message -->
        <d-textarea
          placeholder=${this._translation.translate('contact_form', 'message')}
          ${this._formController.field('message')}
          rows="4"
          no-auto-resize
        ></d-textarea>

        <!-- privacy -->
        <d-flex horizontal crossaxis-center space="xs">
          <d-checkbox
            .value=${this._acceptedPrivacy}
            @value-picked=${(e: ValueEvent<boolean>) => {
              this._acceptedPrivacy = e.detail.value;
            }}
          ></d-checkbox>
          <d-text> ${this._translation.translate('accepts_data_privacy')} </d-text>
        </d-flex>
        <d-flex space="sm">
          <d-text> ${this._translation.translate('contact_form', 'want_mail_for')} </d-text>
          <d-flex horizontal crossaxis-center space="xs">
            <d-checkbox ${this._formController.field('isNewsletter')}></d-checkbox>
            <d-text> ${this._translation.translate('contact_form', 'mail_for_heldenpost')} </d-text>
          </d-flex>
          <d-flex horizontal crossaxis-center space="xs">
            <d-checkbox ${this._formController.field('isWebinar')}></d-checkbox>
            <d-text> ${this._translation.translate('contact_form', 'mail_for_webinar')} </d-text>
          </d-flex>
        </d-flex>
        <d-flex horizontal end>
          <d-button
            .value=${this._acceptedPrivacy}
            ?disabled=${!this._acceptedPrivacy}
            @click=${() => {
              if (!this.repeatEmail) this.repeatEmail = '';
              this._formController.validate();
            }}
          >
            ${this._translation.translate('send')}
          </d-button>
        </d-flex>
      </d-flex>
    `;
  }

  private _initFormController() {
    this._acceptedPrivacy = false;
    this.repeatEmail = undefined;
    return new FormController(this, new ContactFormData({}), {
      success: async (data: ContactFormData) => {
        if (!this.repeatEmail || this.repeatEmail !== data.email) {
          XSnackbar.show('Die E-Mail-Adressen stimmen nicht überein.');
          return;
        }

        const action = async () => {
          if (data.isNewsletter || data.isWebinar) {
            const lead = new Lead({
              ...data.toJSON(),
            });
            await upsertLeadOrContactAndCheckDOI(lead, false)();
          }

          await ContactFormData.sendContactEmail(data)();
        };

        return DubniumDialog.waitFor(action(), 'Kontaktformular wird verschickt...', {
          closeOnNavigate: false,
        }).then(() => {
          this._isFinished = true;
          return triggerSuccessIcon();
        });
      },
      error: err => {
        logger.error(err, 'error in form controller');
      },
      translateError: key => this._translation.translate(key),
    });
  }
}
